import { AbsoluteCenter, Box, Link, Stack } from '@web/components/atoms/Layout'
import { Body2, SectionBody } from '@web/components/atoms/Typography'
import { CommentsLayout, type Comments as CommentsProps } from '@web/graphql-components'
import { useIsHiddenFromDates } from '@web/components/utils/dateUtils'
import { BRAND_KEY, useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { useMergeFields } from '../Text/MergeTags/MergeTag'
import { COMMENTS_MERGE_TAGS } from '../Text/MergeTags/utils'
import AnimatedComments from './AnimatedComments'
import { CommentProps } from './Comment'
import { DialogContent, DialogRoot, DialogTrigger } from '@web/components/atoms/Modal'

const [TotalDonorsTag] = COMMENTS_MERGE_TAGS

const Comments = (
  props: CommentsProps & {
    comments: CommentProps[]
    totalRecords?: number
    isLoading?: boolean
    isMobile?: boolean
    dateToUse?: string | null
    zoom?: number
    ViewAllDialog?: (props: CommentsProps) => ReactElement
    PaginatedComments?: (
      props: CommentsProps & { containerDimensions: number; isHorizontalLayout?: boolean; zoom?: number },
    ) => ReactElement
  },
) => {
  const {
    comments = [],
    layout,
    showTotal,
    showViewAll,
    amountToShowOver,
    start,
    end,
    styles,
    mobileStyles,
    isLoading,
    isMobile,
    totalRecords = 0,
    dateToUse,
    zoom,
    ViewAllDialog,
    PaginatedComments,
  } = props
  const commentsToShow = amountToShowOver ? comments.filter((m) => m.amount >= amountToShowOver) : comments
  const isHidden = useIsHiddenFromDates(start, end, dateToUse)
  const isHorizontalLayout = (isMobile ? (mobileStyles?.layout ?? layout) : layout) === CommentsLayout.Horizontal
  const side = isHorizontalLayout ? 'width' : 'height'
  const backgroundColor = useColorWithOpacity(styles?.backgroundColor, styles?.backgroundColorOpacity, '#FFFFFF')
  const messageBackgroundColor = useColorWithOpacity(
    styles?.messageBackgroundColor,
    styles?.messageBackgroundColorOpacity,
  )
  const messageBorderColor = useColorWithOpacity(styles?.messageBorderColor, styles?.messageBorderColorOpacity)
  const fundraiserLinkColor = useColorWithOpacity(styles?.fundraiserLinkColor, '1', BRAND_KEY)
  const viewAllTextColor = useColorWithOpacity(styles?.viewAllTextColor, '1', '#000000')
  const height = isMobile ? mobileStyles?.height : styles?.height
  const isPixelHeight = height?.includes('px')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerDimensions, setContainerDimensions] = useState(0)

  useEffect(() => {
    useMergeFields.getState().setMergeField(TotalDonorsTag.MergeFieldSettingId, totalRecords.toLocaleString('en'))
  }, [totalRecords])

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    const measure = () => {
      setContainerDimensions(element.getBoundingClientRect()[side])
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [side, isHidden, commentsToShow.length])

  if (isHidden) {
    return null
  }

  if (commentsToShow.length === 0) {
    return (
      <Box
        w="100%"
        h="100%"
        bgColor={backgroundColor} // Vertical with no height we max out the height
        {...(!isHorizontalLayout && !isPixelHeight ? { maxHeight: '500px' } : {})}
        css={{
          '@container page (min-width: 0px)': {
            p: mobileStyles?.padding ?? 0,
          },
          '@container page (min-width: 901px)': {
            p: styles?.padding ?? 0,
          },
        }}
      >
        <Box minHeight="112px">
          {!isLoading && (
            <AbsoluteCenter
              textAlign="center"
              backgroundColor="rgba(255, 255, 255, .8)"
              padding="12px 24px"
              borderRadius="4px"
              w={`min(${isHorizontalLayout ? '400px' : '250px'}, 90%)`}
              h="72px"
              transition="width 0.2s ease-in-out, height 0.2s ease-in-out"
            >
              <AbsoluteCenter w="100%">
                <SectionBody fontSize="16px">
                  No comments - Be the first one and <Link href="#donation-form-element">donate now</Link>
                </SectionBody>
              </AbsoluteCenter>
            </AbsoluteCenter>
          )}
        </Box>
      </Box>
    )
  }

  const showViewAllButton = showViewAll && totalRecords > 0
  const showHeader = showTotal || showViewAllButton

  const commentsStyles = {
    ...styles,
    messageBackgroundColor,
    messageBorderColor,
    fundraiserLinkColor,
  }

  return (
    <Box
      ref={containerRef}
      w="100%"
      bgColor={backgroundColor}
      css={{
        ...(styles?.textColor === 'light'
          ? {
              color: 'var(--branding-color-text-light)',
              '& .comment__message, & .comment__date': { color: 'var(--branding-color-text-light)' },
            }
          : { color: 'var(--branding-color-text-dark)' }),
        '@container page (min-width: 0px)': {
          p: mobileStyles?.padding ?? 0,
        },
        '@container page (min-width: 901px)': {
          p: styles?.padding ?? 0,
        },
      }}
      className="c-component-wrapper"
      h="100%"
      overflow="hidden"
      // Vertical with no height we max out the height
      {...(isHorizontalLayout ? {} : { height: isPixelHeight ? height : `500px` })}
    >
      {showHeader && (
        <Stack
          direction="row"
          justifyContent="space-between"
          w="100%"
          padding={isHorizontalLayout ? '0px 16px 16px 16px' : '16px 0px 16px 0px'}
          css={{
            color:
              styles?.outsideTextColor === 'light'
                ? 'var(--branding-color-text-light)'
                : 'var(--branding-color-text-dark)',
          }}
        >
          {showTotal && <Body2 fontSize="20px">Total Supporters: {totalRecords.toLocaleString('en')}</Body2>}
          {showViewAllButton && (
            <Box margin="0 0 0 auto">
              <DialogRoot lazyMount={true} size="lg" scrollBehavior="inside">
                <DialogTrigger asChild>
                  <Link
                    as="button"
                    id="view-all"
                    css={{ color: viewAllTextColor, borderColor: 'transparent', fontWeight: 600 }}
                  >
                    View All
                  </Link>
                </DialogTrigger>
                {ViewAllDialog && (
                  <DialogContent maxW="890px">
                    <ViewAllDialog {...props} styles={commentsStyles} />
                  </DialogContent>
                )}
              </DialogRoot>
            </Box>
          )}
        </Stack>
      )}
      <Box minHeight="112px" height={`calc(100% - ${showHeader ? 56 : 0}px)`}>
        {styles?.options?.hasAutoPlay === false && PaginatedComments ? (
          containerDimensions > 0 && (
            <PaginatedComments
              {...props}
              styles={commentsStyles}
              isHorizontalLayout={isHorizontalLayout}
              containerDimensions={containerDimensions}
            />
          )
        ) : (
          <AnimatedComments {...props} comments={commentsToShow} zoom={zoom} styles={commentsStyles} />
        )}
      </Box>
    </Box>
  )
}

export default Comments
